import React, { useState, useEffect } from "react";
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
} from "@aws-sdk/client-cognito-identity-provider";
import { jwtDecode } from "jwt-decode";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./baseStyles.css";
import "./LoginPage.css";
const apiUrl = process.env.REACT_APP_API_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    loginUserId: "",
    password: "",
    newPassword: "",
  });
  const [confirmationCode, setConfirmationCode] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const marketingMessages = [
    "Join thousands who start their day with purpose.",
    "Build stronger habits with daily reminders.",
    "Smart tools for smart relationships.",
    "Little nudges, big impact.",
    "Remember what matters most.",
    "Personal motivation delivered daily.",
    "Turn intention into routine.",
    "Fuel your day with focus.",
    "Empower your relationships with simple check-ins.",
  ];

  const [rotatingMessage, setRotatingMessage] = useState("");

  // remove spaces and common invisible chars users paste on mobile
  const sanitizePassword = (pw = "") =>
    pw
      .normalize("NFKC")
      .replace(/[\u200B-\u200D\uFEFF]/g, "") // zero-width chars
      .replace(/\u00A0/g, " ")               // NBSP → space
      .replace(/\s+/g, "");                  // remove ALL whitespace



  useEffect(() => {
    const pickRandom = () => {
      const randomIndex = Math.floor(Math.random() * marketingMessages.length);
      setRotatingMessage(marketingMessages[randomIndex]);
    };
    pickRandom();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]:
        name === "password" || name === "newPassword"
          ? sanitizePassword(value)
          : (value || ""),
    }));
  };


  const handleLogin = async () => {
    if (!form.loginUserId || !form.password) {
      setError("Please enter your Login ID and Password.");
      return;
    }
    setError("");
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });

      const response = await client.send(
        new InitiateAuthCommand({
          AuthFlow: "USER_PASSWORD_AUTH",
          ClientId: "7883r6l59fuadm2gkgtr4tihve",
          AuthParameters: {
            USERNAME: form.loginUserId.trim().toLowerCase(),
            PASSWORD: sanitizePassword(form.password), // ← use sanitized
          },
        })
      );


      const idToken = response.AuthenticationResult.IdToken;
      const accessToken = response.AuthenticationResult.AccessToken;
      const refreshToken = response.AuthenticationResult.RefreshToken;
      const decoded = jwtDecode(idToken);
      const userId = decoded.sub;
      const fullName = decoded.name || "";
      const [firstName, ...rest] = fullName.trim().split(" ");
      const lastName = rest.join(" ") || "";

      // Replace only authentication/user identity values.
      // Do not clear all localStorage because it may contain the
      // pending direct-link destination or other app preferences.
      localStorage.removeItem("idToken");
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("userId");
      localStorage.removeItem("firstName");
      localStorage.removeItem("lastName");
      localStorage.removeItem("email");
      localStorage.removeItem("phone");

      localStorage.setItem("idToken", idToken);
      localStorage.setItem("authToken", accessToken);

      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }

      localStorage.setItem("userId", userId);
      localStorage.setItem("firstName", firstName);
      localStorage.setItem("lastName", lastName);
      localStorage.setItem("email", decoded.email || "");
      localStorage.setItem("phone", decoded.phone_number || "");




      // after login…
      let exists = false;
      try {
        const r = await axios.post(apiUrl, { operation: "fetch_customer", userid: userId }, {
          headers: { Authorization: `Bearer ${idToken}` },
          validateStatus: s => s < 500, // don't throw on 404
        });
        const d = r.data || {};
        exists =
          d.exists === true || d.exists === "true" || d.exists === 1 || d.exists === "1" ||
          !!d.Item || d.found === true || (typeof d.count === "number" && d.count > 0);
      } catch (e) {
        // only unexpected 5xx/network errors reach here
        throw e;
      }

      if (!exists) {
        await axios.post(apiUrl, { operation: "add_customer", userid: userId, loginUserId: form.loginUserId,
          email: decoded.email || "", firstName, lastName, phone: decoded.phone_number || "" },
          { headers: { Authorization: `Bearer ${idToken}` } });
      }







      /* if needed for debugging route when no logged in 
      console.log("Redirecting to:", location.state?.from?.pathname || "/list-manager");*/

      /* navigate("/campaign-manager");*/

      // Prefer the durable destination saved by RequireAuth.
      // This survives a refresh/close of the login page better than
      // React Router state alone.
      const pendingDestination =
        localStorage.getItem("pendingLoginDestination");

      const stateFrom = location.state?.from
        ? `${location.state.from.pathname || ""}${location.state.from.search || ""}${location.state.from.hash || ""}`
        : "";

      const destination =
        pendingDestination ||
        stateFrom ||
        "/list-manager";

      localStorage.removeItem("pendingLoginDestination");

      navigate(destination, { replace: true });



    } catch (err) {
      console.error("Login error:", err);
      if (err.message.includes("not confirmed")) {
        setIsConfirming(true);
        setError("Account not confirmed. Please confirm.");
      } else {
        setError(err.message || "Login failed.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!form.loginUserId) {
      setError("Please enter your Login ID.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });
      await client.send(
        new ForgotPasswordCommand({
          ClientId: "7883r6l59fuadm2gkgtr4tihve",
          Username: form.loginUserId,
        })
      );
      setIsResetting(true);
      setSuccessMessage("Reset code sent! Check your email.");
    } catch (err) {
      setError(err.message || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!form.loginUserId || !resetCode || !form.newPassword) {
      setError("Please fill in all fields.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });

      await client.send(
        new ConfirmForgotPasswordCommand({
          ClientId: "7883r6l59fuadm2gkgtr4tihve",
          Username: form.loginUserId,
          ConfirmationCode: resetCode,
          Password: sanitizePassword(form.newPassword), // ← use sanitized
        })
      );


      setSuccessMessage("Password reset successfully.");
      setIsResetting(false);
    } catch (err) {
      setError(err.message || "Reset failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAccount = async () => {
    if (!form.loginUserId || !confirmationCode) {
      setError("Please enter Login ID and code.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const client = new CognitoIdentityProviderClient({ region: "us-east-1" });
      await client.send(
        new ConfirmSignUpCommand({
          ClientId: "7883r6l59fuadm2gkgtr4tihve",
          Username: form.loginUserId,
          ConfirmationCode: confirmationCode,
        })
      );
      setSuccessMessage("Account confirmed!");
      setIsConfirming(false);
    } catch (err) {
      setError(err.message || "Confirmation failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("idToken");
    localStorage.removeItem("authToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("userId");
    localStorage.removeItem("firstName");
    localStorage.removeItem("lastName");
    localStorage.removeItem("email");
    localStorage.removeItem("phone");
    localStorage.removeItem("pendingLoginDestination");

    navigate("/login", { replace: true });
  };


return (
  <div className="login-page-container">
    <div className="login-card">
      <div className="marketing-section">

        {/*<img src="/myDailyReminders-logo1.png" alt="myDailyReminders Logo" className="login-logo" />*/}

        <div className="login-brand-wrapper">
          <table className="login-brand-table">
            <tbody>
              <tr>
                <td className="login-brand-check-cell">✓</td>
                <td className="login-brand-name-cell">
                  <div className="login-brand-name">myDailyReminders</div>
                </td>
              </tr>
              <tr>
                <td></td>
                <td>
                  <div className="login-brand-tagline">Simple reminders. Smarter relationships.</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <br />

        {/*<p className="footer-message3">{rotatingMessage}</p>*/}
        <p className="footer-message3">
          A daily reminder system built to strengthen habits, guide connection through partner games, and deepen relationships.
        </p>
      </div>

      <div className="form-section">
        <h2>{isConfirming ? "Confirm Account" : isResetting ? "Reset Password" : "Login"}</h2>

        <input
          type="text"
          name="loginUserId"
          autoComplete="username"
          placeholder="Login ID"
          value={form.loginUserId}
          onChange={handleChange}
          onKeyDown={(e) => { if (e.key === " " || e.code === "Space") e.preventDefault(); }}
        />

        {!isConfirming && !isResetting && (
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="Password"
            value={form.password}
            onChange={handleChange}
            onKeyDown={(e) => { if (e.key === " " || e.code === "Space") e.preventDefault(); }}
          />
        )}

        {isConfirming && (
          <input
            type="text"
            name="confirmationCode"
            placeholder="Confirmation Code"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
          />
        )}

        {isResetting && (
          <>
            <input
              type="text"
              name="resetCode"
              placeholder="Reset Code"
              value={resetCode}
              onChange={(e) => setResetCode(e.target.value)}
            />
            <input
              type="password"
              name="newPassword"
              autoComplete="new-password"
              placeholder="New Password"
              value={form.newPassword}
              onChange={handleChange}
              onKeyDown={(e) => { if (e.key === " " || e.code === "Space") e.preventDefault(); }}
            />
          </>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            isConfirming ? handleConfirmAccount() : isResetting ? handleResetPassword() : handleLogin();
          }}
        >
          <button
            type="submit"
            className="save-button"
            disabled={loading}
          >
            {isConfirming ? "Confirm" : isResetting ? "Reset Password" : "Login"}
          </button>

          {!isConfirming && !isResetting && (
            <div className="secondary-options">
              <button type="button" onClick={handleForgotPassword}>Forgot Password?</button>
              <button type="button" onClick={() => navigate("/signup")}>Sign Up</button>
              <button type="button" onClick={() => setIsConfirming(true)}>Confirm Account</button>
            </div>
          )}
        </form>

        {(isConfirming || isResetting) && (
          <div className="secondary-options">
            <button onClick={() => {
              setIsConfirming(false);
              setIsResetting(false);
            }}>Back to Login</button>
          </div>
        )}

        {loading && <div>Loading...</div>}
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {localStorage.getItem("idToken") && (
          <div className="secondary-options" style={{ marginTop: "1rem" }}>
            <button className="link-button" onClick={handleLogout}>Logout</button>
          </div>
        )}

        <br /><br />

        <h4> Our Products </h4>
        <p className="footer-message2">- Personal Reminders - Create your own list of daily reminders — quotes, habits, affirmations, or even jokes. Practice what matters, daily. </p>
        <p className="footer-message2">- Commercial Reminders - We curate reminders from self-help books, popular courses, and more — so you can learn and grow a little every day. </p>
        <p className="footer-message2">- my Favorites - A relationship game where you and your partner answer fun personal questions — then guess each other’s answers. </p>
        <p className="footer-message2">- the Big Question - Answer one deep, thoughtful question each day with your group. Then let AI pick the “best” answer.</p>
      </div>
    </div>
  </div>
);



};

export default LoginPage;